import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Input,
  InputOnChangeData,
  mergeClasses,
  Select,
  Spinner,
} from "@fluentui/react-components";
import { DismissRegular } from "@fluentui/react-icons";
import { createFeedback } from "api";
import { ConstantValues } from "common/constants";
import { CustomToggle } from "components/customToggle/CustomToggle";
import { IConversation } from "models/IConversation";
import { IFeedback } from "models/IFeedback";
import { useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "store";
import {
  useSharedDialogContentStyles,
  useSharedFormStyles,
  useSharedModalStyles,
  useSharedTextStyles,
} from "theme/sharedStyles";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useStyles } from "./FeedbackModal.styles";
import { IFeedbackModalProps } from "./FeedbackModal.types";

export const FeedbackModal = (props: IFeedbackModalProps): JSX.Element => {
  const currentSelectedConversation: IConversation = useSelector(
    (state: RootState) => state.currentConversationSetter.value,
  );
  const currentUser = useSelector(
    (state: RootState) => state.currentUserSlice.value,
  );
  const styles = useStyles();
  const sharedModal = useSharedModalStyles();
  const sharedForm = useSharedFormStyles();
  const sharedText = useSharedTextStyles();
  const sharedContent = useSharedDialogContentStyles();

  const [wantResponse, setWantResponse] = useState<boolean>(true);
  const [requestsAssistance, setRequestsAssistance] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const [feedbackData, setFeedbackData] = useState<IFeedback>({
    feedbackType: props.feedbackType,
    content: ConstantValues.EMPTY_STRING,
    prompt: props.previousMessage.content,
    response: props.message.content,
    llm: props.previousMessage.llmConfig.llmType,
    nhqaEnabled: props.previousMessage.llmConfig.rag,
    hasFiles: props.previousMessage.files.length > 0,
    hasKnowledgeComponents: props.previousMessage.tags?.length
      ? props.previousMessage.tags.length > 0
      : false,
    submittedAt: Date.parse(new Date().toISOString()),
    user: {
      userId: currentSelectedConversation.userId,
      displayName:
        currentUser?.displayName ?? currentSelectedConversation.displayName,
    },
    requestAssistance: requestsAssistance,
  });

  const handleFeedbackChange = (
    _ev: React.ChangeEvent<HTMLInputElement>,
    data: InputOnChangeData,
  ): void => {
    setFeedbackData((prev: IFeedback) => ({
      ...prev,
      content: data.value ?? ConstantValues.EMPTY_STRING,
    }));
  };

  const handleSubmit = async () => {
    if (!feedbackData.content.trim()) {
      setError("Content is required");
      return;
    }

    const feedback: IFeedback = {
      ...feedbackData,
      response: wantResponse
        ? feedbackData.response
        : ConstantValues.EMPTY_STRING,
      requestAssistance: requestsAssistance,
    };
    setError(null);
    setIsSubmitting(true);

    try {
      await createFeedback({ feedback });
      props.onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={props.isOpen} modalType="modal">
      <DialogSurface className={styles.dialogSurface}>
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                icon={<DismissRegular />}
                aria-label="Close"
                onClick={props.onClose}
              />
            }
          >
            Submit Feedback to AIMS Team
          </DialogTitle>
          <DialogContent
            className={mergeClasses(
              sharedModal.modalBody,
              sharedContent.formContainer,
              styles.dialogContentOverride,
            )}
          >
            {isSubmitting && (
              <div className={sharedContent.formLoadingOverlay}>
                <Spinner label="Submitting feedback..." />
              </div>
            )}
            <div>
              <div
                className={mergeClasses(
                  sharedForm.label,
                  styles.fieldLabelOverride,
                )}
              >
                User
              </div>
              <div
                className={mergeClasses(
                  sharedText.body,
                  styles.fieldValueColor,
                )}
              >
                {feedbackData.user.displayName}
              </div>
            </div>

            <div>
              <div
                className={mergeClasses(
                  sharedForm.label,
                  styles.fieldLabelOverride,
                )}
              >
                Feedback Type
              </div>
              <div
                className={mergeClasses(
                  sharedText.body,
                  styles.fieldValueColor,
                )}
              >
                {feedbackData.feedbackType === "like" ? "Positive" : "Negative"}
              </div>
            </div>

            <Field
              required
              label={
                feedbackData.feedbackType === "like"
                  ? "What did you like?"
                  : "What did you dislike?"
              }
              validationState={error ? "error" : "none"}
              validationMessage={error ?? undefined}
            >
              <Input
                id="feedback-content"
                value={feedbackData.content}
                onChange={handleFeedbackChange}
              />
            </Field>

            {feedbackData.feedbackType === "dislike" && (
              <Field label="Would you like assistance with this feedback?">
                <Select
                  value={requestsAssistance ? "yes" : "no"}
                  onChange={(_ev, data) =>
                    setRequestsAssistance(data.value === "yes")
                  }
                  className={styles.assistanceSelect}
                >
                  <option value="no">No</option>
                  <option value="yes">Yes</option>
                </Select>
              </Field>
            )}

            <div>
              <div
                className={mergeClasses(
                  sharedForm.label,
                  styles.fieldLabelOverride,
                )}
              >
                Prompt
              </div>
              <div
                className={mergeClasses(
                  sharedText.body,
                  styles.fieldValueColor,
                )}
              >
                {feedbackData.prompt}
              </div>
            </div>

            <CustomToggle
              checked={wantResponse}
              onChange={setWantResponse}
              label="Include response?"
            />

            {wantResponse && (
              <div>
                <div
                  className={mergeClasses(
                    sharedForm.label,
                    styles.fieldLabelOverride,
                  )}
                >
                  Response
                </div>
                <div className={styles.responseContainer}>
                  <Markdown remarkPlugins={[remarkGfm]}>
                    {feedbackData.response}
                  </Markdown>
                </div>
              </div>
            )}
          </DialogContent>
          <DialogActions className={styles.buttonContainer}>
            <Button
              onClick={handleSubmit}
              appearance="primary"
              disabled={isSubmitting}
            >
              Submit
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
